//Refactored for node upgrade

import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { LayoutProvider } from '@celito.clients/context';
import { initAxios } from '@celito.clients/network';
import {
  RecordNavigationProvider,
  ToastProvider,
} from '@celito.clients/provider';
import { logger } from '@celito.clients/services';
import { CustomRouter, Loader } from '@celito.clients/shared';
import {
  MsalInstanceManager,
  TenantConfigManager,
} from '@celito.clients/singleton';
import { lightTheme, ThemeProvider } from '@celito.clients/theme';
import { history } from '@celito.clients/utils';
import { FluentProvider } from '@fluentui/react-components';
import {
  decideBoot,
  fetchTenantConfigWithTimeout,
} from 'libs/core/src/config/boot-config';
import { clearLoginAttempts } from 'libs/core/src/config/login-loop-guard';
import queryClient from 'libs/core/src/config/query-client';
import { setTenantConfigApiBase } from 'libs/core/src/config/tenant-config';
import { useEffect, useState } from 'react';
import * as ReactDOM from 'react-dom/client';
import { QueryClientProvider } from 'react-query';

import App from './app/app';
import BootErrorBoundary from './app/boot-error-boundary';
import { BootErrorView } from './screens/boot-error';
import { TenantErrorVariant, TenantErrorView } from './screens/tenant-error';

/* catch preload event */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
window.addEventListener('vite:preloadError', (event: any) => {
  logger.error(event?.payload ?? 'Dynamic import failed');
  window.location.reload();
});

const root = ReactDOM.createRoot(document.getElementById('root')!);

/**
 * WI-F4 — Boot sequence (ADO #38532).
 *
 * Order: render loader → fetch TenantConfig by hostname (WI-F1, bounded
 * timeout) → on success construct the MSAL manager WITH the config (WI-F3),
 * initMsal(), then MsalProvider → app. A config-fetch failure renders the
 * tenant-error screen (WI-F1) with the correct variant — a state DISTINCT from
 * an MSAL-init failure, which falls through to ErrorOrUnauthorized.
 */
type BootState =
  | { phase: 'loading' }
  | { phase: 'ready'; instance: PublicClientApplication }
  | { phase: 'tenant-error'; variant: TenantErrorVariant }
  | { phase: 'msal-error'; error: Error };

const Main = () => {
  const [state, setState] = useState<BootState>({ phase: 'loading' });

  useEffect(() => {
    let cancelled = false;

    const boot = async () => {
      // 1) Fetch runtime tenant config (bounded so a hung request surfaces as
      //    config-unavailable instead of stalling the loader forever).
      //    Belt-and-suspenders: the client is designed never to reject, but
      //    boot must never hang/crash — map any thrown error to
      //    config-unavailable so we always render a deterministic screen.
      let result: Awaited<ReturnType<typeof fetchTenantConfigWithTimeout>>;
      try {
        result = await fetchTenantConfigWithTimeout();
      } catch (err) {
        logger.error(err as Error, { origin: 'Tenant Config Boot' });
        result = {
          status: 'config-unavailable',
          reason: 'Tenant config fetch threw unexpectedly during boot.',
        };
      }
      if (cancelled) return;

      const decision = decideBoot(result);
      if (decision.kind === 'tenant-error') {
        // Config-fetch failure — distinct from an MSAL-init failure.
        setState({ phase: 'tenant-error', variant: decision.variant });
        return;
      }

      // 2) Config resolved: publish it to the runtime store (WI-F7 — the
      //    provider-agnostic e-sign flow reads `TenantConfig.esign` from here
      //    at signing time), initialize the network layer (moved here from
      //    app.tsx, after the config fetch), then construct + init MSAL with
      //    the runtime TenantConfig.
      try {
        TenantConfigManager.getInstance().setConfig(decision.config);
        setTenantConfigApiBase(decision.config.apiBaseUrl);
        initAxios();
        const msalManager = MsalInstanceManager.getInstance();
        await msalManager.initMsal(decision.config);
        if (cancelled) return;
        setState({ phase: 'ready', instance: msalManager.getMsalInstance() });
      } catch (err) {
        logger.error(err as Error, { origin: 'MSAL Bootstrap Error' });
        if (!cancelled) setState({ phase: 'msal-error', error: err as Error });
      }
    };

    boot();

    return () => {
      cancelled = true;
    };
  }, []);

  if (state.phase === 'tenant-error') {
    return <TenantErrorView variant={state.variant} />;
  }

  if (state.phase === 'msal-error') {
    // MSAL bootstrap/token failure — show an explicit, loud error screen with a
    // single Retry (clears the login-loop guard so the reload gets a clean
    // attempt) instead of a blank page or an unbounded loginRedirect loop.
    return (
      <BootErrorView
        error={state.error}
        onRetry={() => {
          clearLoginAttempts();
          window.location.reload();
        }}
      />
    );
  }

  if (state.phase === 'loading') {
    return (
      <FluentProvider theme={lightTheme}>
        <ThemeProvider>
          <div
            style={{ display: 'grid', placeContent: 'center', height: '100vh' }}
          >
            <Loader withLabel />
          </div>
        </ThemeProvider>
      </FluentProvider>
    );
  }

  return (
    <MsalProvider instance={state.instance}>
      <QueryClientProvider client={queryClient}>
        <CustomRouter history={history}>
          <FluentProvider theme={lightTheme}>
            <ThemeProvider>
              <ToastProvider>
                <LayoutProvider>
                  <RecordNavigationProvider>
                    <App />
                  </RecordNavigationProvider>
                </LayoutProvider>
              </ToastProvider>
            </ThemeProvider>
          </FluentProvider>
        </CustomRouter>
      </QueryClientProvider>
    </MsalProvider>
  );
};

root.render(
  <BootErrorBoundary>
    <Main />
  </BootErrorBoundary>
);
